// /home/szymon/kreator2/frontend/src/components/Editor.js

import React, { useEffect, useMemo, useCallback, useState, useRef } from "react";
import { useEditorState } from "./editor/EditorState";
import EditorTopBar from "./editor/EditorTopBar";
import EditorSidePanel from "./editor/EditorSidePanel";
import EditorCanvas from "./editor/EditorCanvas";
import EditorOverlay from "./editor/EditorOverlay";
// Usunięto nieużywane importy ikon
import { useEditorShortcuts } from "./editor/useEditorShortcuts";
import { useEditorActions } from "./editor/useEditorActions";
import { useLocalHistory } from "../hooks/useLocalHistory";
import { useEditorLocalCallbacks } from "./editor/hooks/useEditorLocalCallbacks";
import { useEditorZoom } from "./editor/hooks/useEditorZoom";
import { useEditorPersonalization } from "./editor/hooks/useEditorPersonalization";
import EditorPersonalizePanel from "./editor/EditorPersonalizePanel";
import { parseGuideExpression, calculateNearestGuideInfo } from "./editor/GuideUtils"; // Połączono importy z GuideUtils
import { buildSnapGuides } from "./editor/snapUtils"; // Poprawiony, pojedynczy import buildSnapGuides
import { useFakeRebusDragDrop } from "./editor/hooks/useFakeRebusDragDrop";
import EditorLeftToolbar from "./editor/EditorLeftToolbar";
import HelpPopup from "./editor/HelpPopup"; // Import popupa pomocy
import ClientHelpPage from "./ClientHelpPage"; // Import treści strony pomocy
// Usunięto import useEditorAutoSnap
import { calculateCenteredX } from "./editor/snapUtils"; // Potrzebne do logiki auto-centrowania

// useEffect for automatically switching panel mode when a text object is selected
import { useEffect as useSelectionEffect } from 'react';


export default function Editor({
  backendUrl,
  pdfData,
  projectId,
  onGoBack,
  orderPublicKey, // Zmieniono nazwę propa na bardziej opisową
  downloadKey,
  isAccepted = false,
  onAcceptProject, // Dodano odbieranie nowego propa
  tooltipsEnabled, // Nowy prop
  setTooltipsEnabled, // Nowy prop
  isPersonalizedProject // Odbieramy flagę boolean z ClientPdfEditor
}) {
  // Stan do zarządzania widocznością popupa pomocy
  const [isHelpPopupOpen, setIsHelpPopupOpen] = useState(false);
  // Stan do śledzenia procesu auto-centrowania
  const [isAutoCentering, setIsAutoCentering] = useState(false);
  const [autoCenteringProgress, setAutoCenteringProgress] = useState(0); // Progress 0-100

  const {
    pages,
    setPages,
    currentPage,
    setCurrentPage,
    fontsAreLoaded,
    availableFonts,
    assetsAreLoaded,
    assetsTotal,
    assetsCount,
    selectedShapeId,
    setSelectedShapeId,
    panelMode,
    setPanelMode,
    editingTextId,
    setEditingTextId,
    forceRefresh,
    doAutoSave,
    handleManualSave,
    changesCountRef,
    publicMode,
    handleAddGuide,
    handleRemoveGuide,
    handleChangeGuide,
    loadingPages,
    triggerForceRefresh // Pobieramy nową funkcję
  } = useEditorState({
    backendUrl,
    pdfData,
    projectId,
    downloadKey,
    isAccepted
  });

  const {
    handleChangeText,
    handleChangeImage,
    handleChangeSvg,
    undo,
    redo,
    canUndo,
    canRedo
  } = useEditorActions({
    pages,
    setPages,
    backendUrl,
    publicMode,
    isAccepted,
    doAutoSave,
    changesCountRef,
    currentPage
  });

  const { pushToHistory } = useLocalHistory();

  const {
    foundObj,
    handleAddNewTextLocal,
    handleImageDroppedLocal,
    handleChangeTextLocal,
    handleChangeImageLocal,
    handleChangeSvgLocal,
    handleColorChangeLocal,
    handleLayerUpLocal,
    handleLayerDownLocal,
    handleMoveToTopLocal,
    handleMoveToBottomLocal,
    handleToggleFlagLocal
  } = useEditorLocalCallbacks({
    pages,
    setPages,
    currentPage,
    selectedShapeId,
    setSelectedShapeId,
    pushToHistory,
    changesCountRef,
    publicMode,
    isAccepted,
    doAutoSave,
    handleManualSave,
    backendUrl
  });

  const { currentZoom, handleZoomChange } = useEditorZoom({ pages, currentPage });

  const {
    personalizationText,
    setPersonalizationText,
    isLoadingGuests, // Poprawiona destrukturyzacja
    panelModeForPersonalize,
    handleLeftMenuClick,
    hasPersonalize,
    handleManualSaveLocal
  } = useEditorPersonalization({
    pages,
    setPages, // Przekazujemy setPages
    currentPage,
    panelMode,
    setPanelMode,
    publicMode,
    selectedShapeId,
    setSelectedShapeId,
    handleManualSave,
    doAutoSave,
    changesCountRef,
    // --- Dodanie brakujących propsów ---
    projectId,
    backendUrl,
    downloadKey // Klucz pobierania pliku (dla trybu publicznego)
    // publicAccessKey nie jest potrzebny bezpośrednio w hooku, bo używamy projectId lub downloadKey
  });

  // Automatically switch to text panel when a text object is selected
  useSelectionEffect(() => {
    if (foundObj && foundObj._type === 'text') {
      // Usunięto warunek `if (panelMode !== 'personalize')`
      // Teraz zawsze przełączy na 'text' po kliknięciu obiektu tekstowego
      setPanelMode('text');
    }
  }, [foundObj, setPanelMode]); // Usunięto panelMode z zależności, bo nie jest już potrzebny w warunku

  // Usunięto obliczanie isPersonalizedProject za pomocą useMemo - używamy propa
  // Usunięto log dla isPersonalizedProject
  // console.log("[Editor] Received isPersonalizedProject prop:", isPersonalizedProject);

  const stageRef = useRef(null);

  // ================ Użycie hooka Fake Rebus Drag & Drop ================
  const handleRebusDrop = useCallback(({ file, width, height, x, y }) => {
    console.log("[Editor] handleRebusDrop (from hook) => dropping rebus now.");
    console.log("[Editor] Calling handleImageDroppedLocal with:", { isRebus: true, file, customPos: { x, y }, customSize: { w: width, h: height } });
    try {
      handleImageDroppedLocal({
        isRebus: true,
        file: file,
        customPos: { x, y },
        customSize: { w: width, h: height }
      });
    } catch (error) {
      console.error("[Editor] Error calling handleImageDroppedLocal:", error);
    }
    setTimeout(() => {
        console.log("[Editor] Triggering force refresh after rebus drop (delayed).");
        triggerForceRefresh();
    }, 50);
  }, [handleImageDroppedLocal, triggerForceRefresh]);

  const {
    fakeDraggingRebus,
    handleFakeRebusMouseDown,
    handleCanvasInsideChange,
    handleCanvasPosChange,
    setFakeDraggingRebus
  } = useFakeRebusDragDrop({ onDrop: handleRebusDrop }); // Przekazujemy handleRebusDrop jako onDrop
  // ================ KONIEC Fake Rebus Drag & Drop ================


  const safeCurrentPage = useMemo(() => {
    if (!pages || pages.length === 0) return 0;
    let safeIndex = currentPage;
    if (safeIndex >= pages.length) safeIndex = pages.length - 1;
    if (safeIndex < 0) safeIndex = 0;
    return safeIndex;
  }, [pages, currentPage]);

  useEffect(() => {
    const newSafeIndex = safeCurrentPage;
    if (currentPage !== newSafeIndex && pages && pages.length > 0) {
      console.warn(`[Editor] Correcting currentPage from ${currentPage} to ${newSafeIndex}`);
      setCurrentPage(newSafeIndex);
    }
  }, [safeCurrentPage, currentPage, setCurrentPage, pages]);


  // Funkcja do automatycznego centrowania na wszystkich stronach
  const handleAutoCenterAllPages = useCallback(async () => {
    if (!pages || pages.length === 0 || isAutoCentering) {
      return;
    }
    console.log("[AutoCenter] Rozpoczynanie automatycznego centrowania...");
    setIsAutoCentering(true);
    setAutoCenteringProgress(0);
    const originalPage = currentPage; // Zapamiętaj bieżącą stronę

    try {
      for (let i = 0; i < pages.length; i++) {
        if (i !== currentPage) { // Nie ma potrzeby przeładowywać bieżącej strony
           setCurrentPage(i);
           // Poczekaj chwilę, aby dać czas na render i pomiar
           // Można dostosować opóźnienie w zależności od potrzeb
           await new Promise(resolve => setTimeout(resolve, 150));
        }
        // Aktualizuj postęp
        setAutoCenteringProgress(Math.round(((i + 1) / pages.length) * 100));
      }

      // Zawsze wracaj do PIERWSZEJ strony po zakończeniu pętli
      console.log("[AutoCenter] Pętla zakończona. Ustawianie strony na 0...");
      setCurrentPage(0);
      await new Promise(resolve => setTimeout(resolve, 100)); // Krótka pauza na odświeżenie

      console.log("[AutoCenter] Zakończono pętlę stron. Zapisywanie zmian...");
      handleManualSave(pages); // Zapisz potencjalne zmiany po centrowaniu
      console.log("[AutoCenter] Zapisano zmiany.");

    } catch (error) {
       console.error("[AutoCenter] Błąd podczas automatycznego centrowania:", error);
    } finally {
       setIsAutoCentering(false);
       setAutoCenteringProgress(0);
       console.log("[AutoCenter] Zakończono proces.");
       // Można dodać powiadomienie dla użytkownika, np. alert('Automatyczne centrowanie zakończone.');
    }
  }, [pages, currentPage, setCurrentPage, handleManualSave, isAutoCentering]);

  // --- USUNIĘTO useEffect odpowiedzialny za centrowanie w Editor.js ---
  // Logika centrowania została przeniesiona do EditableText.js,
  // aby używać aktualnych wymiarów węzła Konva.

  // Efekt do uruchomienia automatycznego centrowania po załadowaniu
  const autoCenterTriggeredRef = useRef(false); // Ref do śledzenia, czy już uruchomiono
  useEffect(() => {
    // Sprawdź, czy wszystko załadowane, czy projekt spersonalizowany i czy jeszcze nie uruchomiono
    if (fontsAreLoaded && assetsAreLoaded && isPersonalizedProject && !autoCenterTriggeredRef.current && pages.length > 0) {
      console.log("[Editor Load] Wykryto załadowany projekt spersonalizowany. Uruchamianie auto-centrowania...");
      autoCenterTriggeredRef.current = true; // Oznacz jako uruchomione
      handleAutoCenterAllPages();
    }
  }, [fontsAreLoaded, assetsAreLoaded, isPersonalizedProject, pages, handleAutoCenterAllPages]); // Dodano pages i handleAutoCenterAllPages do zależności


  useEditorShortcuts({
    selectedShapeId,
    isAccepted,
    publicMode,
    setSelectedShapeId,
    updatePages: setPages,
    currentPage: safeCurrentPage,
    undo,
    redo
  });

  const stillLoading = !fontsAreLoaded || !assetsAreLoaded || loadingPages;
  if (stillLoading) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          alignItems: "center",
          justifyContent: "center",
          color: "#333"
        }}
      >
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
        <div
          style={{
            width: "40px",
            height: "40px",
            border: "6px solid #ccc",
            borderTop: "6px solid #666",
            borderRadius: "50%",
            animation: "spin 1s linear infinite"
          }}
        />
        <div style={{ marginTop: "10px", fontSize: "16px" }}>Ładowanie kreatora...</div>
      </div>
    );
  }

  if (!pages || pages.length === 0) {
    return <div style={{ padding: 20 }}>Brak stron do wyświetlenia.</div>;
  }
  const currentPg = pages[safeCurrentPage];
  if (!currentPg) {
    return <div style={{ padding: 20 }}>Błąd: Nie można załadować danych bieżącej strony.</div>;
  }

  function handleNextPage() {
    if (pages.length > 0 && safeCurrentPage < pages.length - 1) {
      const nextPage = safeCurrentPage + 1;
      handleManualSave(pages);
      setCurrentPage(nextPage);
      setTimeout(() => {
        console.log("[Editor] Triggering force refresh after next page.");
        triggerForceRefresh();
        setTimeout(() => {
          console.log("[Editor] Additional force refresh after page change (300ms delay)");
          triggerForceRefresh();
        }, 300);
      }, 100);
    }
  }

  function handlePrevPage() {
    if (safeCurrentPage > 0) {
      const prevPage = safeCurrentPage - 1;
      handleManualSave(pages);
      setCurrentPage(prevPage);
      setTimeout(() => {
        console.log("[Editor] Triggering force refresh after prev page.");
        triggerForceRefresh();
        setTimeout(() => {
          console.log("[Editor] Additional force refresh after page change (300ms delay)");
          triggerForceRefresh();
        }, 300);
      }, 100);
    }
  }

  // Handler do otwierania/zamykania popupa pomocy
  // Usunięto useCallback, aby sprawdzić, czy to rozwiąże problem z hookami
  function handleHelpClick() {
    setIsHelpPopupOpen(prevState => !prevState);
  }
  // const handleHelpClick = useCallback(() => {
  //   setIsHelpPopupOpen(prevState => !prevState);
  // }, []);


  return (
    <div style={{ display: "flex", flexDirection: "column", width: "100%", height: "100vh", overflow: "hidden" }}>
      {/* Popup Pomocy */}
      <HelpPopup isOpen={isHelpPopupOpen} onClose={handleHelpClick}>
        <ClientHelpPage />
      </HelpPopup>

      <EditorTopBar
        onManualSave={handleManualSaveLocal}
        projectId={projectId}
        zoomFactor={currentZoom}
        onZoomChange={handleZoomChange}
        currentPage={safeCurrentPage}
        pageCount={pages.length}
        onPrevPage={handlePrevPage}
        onNextPage={handleNextPage}
        onGoBack={onGoBack}
        publicMode={publicMode}
        onToggleFlag={handleToggleFlagLocal}
        selectedObject={foundObj}
        publicKey={orderPublicKey}
        downloadKey={downloadKey}
         backendUrl={backendUrl}
         isAccepted={isAccepted}
         onAcceptProject={onAcceptProject}
         onChangeText={handleChangeText}
         onChangeSvg={handleChangeSvg}
        undo={undo}
        redo={redo}
        canUndo={canUndo}
        canRedo={canRedo}
        tooltipsEnabled={tooltipsEnabled}
        setTooltipsEnabled={setTooltipsEnabled}
      />

      {/* Główny obszar roboczy poniżej paska górnego */}
      <div style={{ flex: 1, display: "flex", backgroundColor: "#ffffff", overflow: "hidden" }}>
        <EditorLeftToolbar
           panelModeForPersonalize={panelModeForPersonalize}
           handleLeftMenuClick={handleLeftMenuClick}
           hasPersonalize={hasPersonalize}
           tooltipsEnabled={tooltipsEnabled}
           isPersonalizedProject={isPersonalizedProject} // Przekazujemy obliczoną flagę
           publicMode={publicMode} // Przekazujemy publicMode
           onHelpClick={handleHelpClick} // Przekazujemy handler
         />

        {/* Kontener dla panelu bocznego i canvasu */}
        <div style={{ flex: 1, display: "flex", height: '100%' }}> {/* Zapewnia, że ten div zajmuje całą wysokość */}

           {/* Kontener Panelu Bocznego (z własnym przewijaniem) */}
           <div style={{ width: 300, flex: "0 0 auto", height: "100%", backgroundColor: "#fff", overflowY: "auto", borderRight: '1px solid #dee2e6' }}>
             {/* Renderuj panel personalizacji tylko jeśli tryb to 'personalize' ORAZ projekt MA pola do personalizacji */}
             {panelMode === "personalize" && hasPersonalize ? (
               <EditorPersonalizePanel
                 personalizationText={personalizationText} // Przekazujemy tekst
                 setPersonalizationText={setPersonalizationText} // Przekazujemy funkcję do zmiany tekstu
                 handleManualSaveLocal={handleManualSaveLocal} // Poprawiona nazwa propa
                 isLoadingGuests={isLoadingGuests} // Dodano brakujący prop
                 onGoBack={onGoBack}
                 tooltipsEnabled={tooltipsEnabled}
               />
             ) : (
               <EditorSidePanel
                backendUrl={backendUrl}
                panelMode={panelMode}
                setPanelMode={setPanelMode}
                selectedShapeId={selectedShapeId}
                selectedObject={foundObj}
                onAddNewText={handleAddNewTextLocal}
                onImageDropped={handleImageDroppedLocal}
                onChangeText={handleChangeTextLocal}
                availableFonts={availableFonts}
                onLayerUp={handleLayerUpLocal}
                onLayerDown={handleLayerDownLocal}
                onLayerTop={handleMoveToTopLocal}
                onLayerBottom={handleMoveToBottomLocal}
                onChangeImage={handleChangeImageLocal}
                onChangeSvg={handleChangeSvgLocal}
                publicMode={publicMode}
                onChangeColor={handleColorChangeLocal}
                onAddGuide={handleAddGuide}
                onRemoveGuide={handleRemoveGuide}
                onChangeGuide={handleChangeGuide}
                currentPageData={currentPg}
                onRebusFakeDndStart={handleFakeRebusMouseDown}
                tooltipsEnabled={tooltipsEnabled}
              />
            )}
          </div>

          {/* Kontener Canvas (z własnym przewijaniem) */}
          <div
            style={{
              flex: 1,
              overflow: "auto", // Canvas ma własne przewijanie
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start", // Zmieniono z center na flex-start, aby canvas był na górze
              padding: "20px",
              backgroundColor: "#ffffff" // Tło obszaru canvas (zmieniono na biały)
            }}
          >
            <EditorCanvas
              pages={pages}
              currentPage={safeCurrentPage}
              zoomFactor={currentZoom}
              selectedShapeId={selectedShapeId}
              setSelectedShapeId={setSelectedShapeId}
              editingTextId={editingTextId}
              setEditingTextId={setEditingTextId}
              handleChangeText={handleChangeTextLocal}
              handleChangeImage={handleChangeImageLocal}
              handleChangeSvg={handleChangeSvgLocal}
              assetsAreLoaded={assetsAreLoaded}
              assetsTotal={assetsTotal}
              assetsCount={assetsCount}
              forceRefresh={forceRefresh}
              publicMode={publicMode}
              guides={currentPg.guides || []}
              fontsAreLoaded={fontsAreLoaded}
              onRebusDropped={handleRebusDrop} // Zmieniono nazwę propa
              fakeDraggingRebus={fakeDraggingRebus}
              setFakeDraggingRebus={setFakeDraggingRebus}
              onCanvasInsideChange={handleCanvasInsideChange}
              onCanvasPosChange={handleCanvasPosChange}
              stageRef={stageRef}
              isProjectPersonalized={isPersonalizedProject} // Przekazujemy flagę dalej
              // Przekazujemy wymiary bazowe do Canvas (usunięto zduplikowany guides)
              baseWidth={currentPg.baseWidth}
              baseHeight={currentPg.baseHeight}
              backendUrl={backendUrl} // Przekazujemy backendUrl
            />
          </div>
        </div>
      </div>

      <EditorOverlay
        showOverlay={!fontsAreLoaded || !assetsAreLoaded || loadingPages}
        fontsAreLoaded={fontsAreLoaded}
        assetsAreLoaded={assetsAreLoaded}
        assetsCount={assetsCount}
        assetsTotal={assetsTotal}
        // Dodajemy propsy do wyświetlania stanu auto-centrowania
        isAutoCentering={isAutoCentering}
        autoCenteringProgress={autoCenteringProgress}
      />
    </div>
  );
}
